// src/components/admin/flows/panels/ApprovalConfigForm.js
import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';

const ApprovalConfigForm = ({ config, onChange }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const inputClass = `w-full px-3 py-2 rounded-lg text-sm border ${
        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
    } focus:ring-2 focus:ring-blue-500 focus:outline-none`;

    const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

    return (
        <div className="space-y-4">
            {/* Approver Type */}
            <div>
                <label className={labelClass}>Approver</label>
                <select
                    value={config.approvers?.type || 'reporting_manager'}
                    onChange={(e) => onChange({
                        ...config,
                        approvers: { ...config.approvers, type: e.target.value }
                    })}
                    className={inputClass}
                >
                    <option value="reporting_manager">Reporting Manager</option>
                    <option value="specific_user">Specific User</option>
                    <option value="role">By Role</option>
                </select>
            </div>

            {/* Specific user/role input */}
            {config.approvers?.type === 'specific_user' && (
                <div>
                    <label className={labelClass}>User ID or Email</label>
                    <input
                        type="text"
                        value={config.approvers?.value || ''}
                        onChange={(e) => onChange({
                            ...config,
                            approvers: { ...config.approvers, value: e.target.value }
                        })}
                        className={inputClass}
                        placeholder="Enter user email..."
                    />
                </div>
            )}

            {config.approvers?.type === 'role' && (
                <div>
                    <label className={labelClass}>Role</label>
                    <select
                        value={config.approvers?.value || ''}
                        onChange={(e) => onChange({
                            ...config,
                            approvers: { ...config.approvers, value: e.target.value }
                        })}
                        className={inputClass}
                    >
                        <option value="">Select role...</option>
                        <option value="HR Admin">HR Admin</option>
                        <option value="Super Admin">Super Admin</option>
                        <option value="Manager">Manager</option>
                    </select>
                </div>
            )}

            {/* Approval Mode */}
            <div>
                <label className={labelClass}>Approval Mode</label>
                <select
                    value={config.approval_type || 'any'}
                    onChange={(e) => onChange({ ...config, approval_type: e.target.value })}
                    className={inputClass}
                >
                    <option value="any">Any one approver</option>
                    <option value="all">All approvers must approve</option>
                    <option value="sequential">Sequential (in order)</option>
                </select>
                <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {config.approval_type === 'any' && 'The first approver to respond determines the outcome'}
                    {config.approval_type === 'all' && 'All approvers must approve for the step to pass'}
                    {config.approval_type === 'sequential' && 'Approvers are notified one at a time in order'}
                </p>
            </div>

            {/* Auto-Approve */}
            <div>
                <label className={labelClass}>Auto-Approve After (hours)</label>
                <input
                    type="number"
                    value={config.auto_approve_after_hours || ''}
                    onChange={(e) => onChange({
                        ...config,
                        auto_approve_after_hours: e.target.value ? parseInt(e.target.value) : null
                    })}
                    className={inputClass}
                    placeholder="Leave empty to disable"
                    min={1}
                />
                <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Automatically approve if no response within the specified hours
                </p>
            </div>

            {/* Escalation */}
            <div className={`border-t pt-4 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <p className={`text-xs font-semibold uppercase tracking-wider mb-3 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Escalation
                </p>

                <div className="space-y-3">
                    <div>
                        <label className={labelClass}>Escalate After (hours)</label>
                        <input
                            type="number"
                            value={config.escalate_after_hours || ''}
                            onChange={(e) => onChange({
                                ...config,
                                escalate_after_hours: e.target.value ? parseInt(e.target.value) : null
                            })}
                            className={inputClass}
                            placeholder="Leave empty to disable"
                            min={1}
                        />
                    </div>

                    {config.escalate_after_hours && (
                        <div>
                            <label className={labelClass}>Escalate To</label>
                            <select
                                value={config.escalate_to?.type || 'skip_level_manager'}
                                onChange={(e) => onChange({
                                    ...config,
                                    escalate_to: { type: e.target.value, value: config.escalate_to?.value || '' }
                                })}
                                className={inputClass}
                            >
                                <option value="skip_level_manager">Skip-Level Manager</option>
                                <option value="specific_user">Specific User</option>
                                <option value="role">By Role</option>
                            </select>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ApprovalConfigForm;
